import { Button, IconButton } from '@dropbox/dig-components/buttons';
import { FormHelperText, FormRow } from '@dropbox/dig-components/form_row';
import { ProgressBar } from '@dropbox/dig-components/progress_indicators';
import { Truncate } from '@dropbox/dig-components/truncate';
import { Text } from '@dropbox/dig-components/typography';
import { Box, Split, Stack } from '@dropbox/dig-foundations';
import { UIIcon } from '@dropbox/dig-icons';
import { DeleteLine, FileLine, UploadLine } from '@dropbox/dig-icons/assets';
import {
  AttachmentSlot as AttachmentSlotObject,
  type ResponseError,
} from '@dropbox/sign-internal-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import React from 'react';
import {
  defineMessages,
  FormattedMessage,
  IntlShape,
  useIntl,
} from 'react-intl';
import { useFeatureFlag } from 'signer-app/context/feature-flags';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import styles from 'signer-app/parts/attachments/attachment-slot.module.css';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { SignatureRequestResponse } from 'signer-app/signer-experience/types';
import { getCSRFToken } from 'signer-app/utils/hs-fetch';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';
import { useBundleProps } from 'signer-app/bundle-props';
import {
  AttachmentImageResizeError,
  isCompressibleAttachmentImage,
  resizeAttachmentImage,
} from './resize-attachment-image';

interface Props {
  attachment: AttachmentSlotObject;
}

// Must match HFSupportedFiles::MAX_ATTACHMENT_FILE_SIZE on the backend
export const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1_000_000;

// Supported file extensions based on backend mime types
export const SUPPORTED_EXTENSIONS = [
  'gif',
  'jpg',
  'jpeg',
  'png',
  'tif',
  'tiff',
  'heic',
  'webp',
  'pdf',
];
const SUPPORTED_MIME_TYPES = [
  'image/gif',
  'image/jpeg',
  'image/pjpeg',
  'image/png',
  'image/tiff',
  'image/x-tiff',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/pdf',
];

const messages = defineMessages({
  invalidFileType: {
    id: 'd2ceb00023790d0b92b0653117ed6f4a457a806c947e88efe790c803e8ac27f3',
    description: 'Invalid file type error message',
    defaultMessage: 'Invalid file type. Please upload one of: {allowedTypes}',
  },
  fileSizeExceeded: {
    id: 'f0b4bcc8dc1e0095b12bacb5ab31c71cb0739d51b39f5de32f4939747d1a178b',
    description: 'File size exceeded error message',
    defaultMessage:
      'File size ({fileSize}MB) exceeds the maximum limit of {maxSize}MB',
  },
  unknownError: {
    id: '3397f102ea65707015831fb339836ffeaec3bfd7a1ba770510b842ba318bf632',
    description: 'Unknown error message',
    defaultMessage: 'An unknown error occurred',
  },
  imageDecodeFailed: {
    id: '45c59bd14fb06eafe42cbd73beb3a6f8f9d82d74590989d6e8ac69bb464dd460',
    description: 'Error shown when an attachment image cannot be decoded',
    defaultMessage:
      'This image could not be read. Please choose a valid JPEG or PNG file.',
  },
  imageResizeFailed: {
    id: 'bc72481855737f470eceb3c437732970674771f5a7b437b586a6f97a3e2afb1b',
    description: 'Error shown when an attachment image cannot be resized',
    defaultMessage:
      'This image could not be resized. Please choose a different JPEG or PNG file.',
  },
  resizedImageTooLarge: {
    id: '76bae494ceffc538e3f403506381fb50f75a0989f9a5cbbdc811a22f0e2230ab',
    description:
      'Error shown when a resized attachment image is still too large',
    defaultMessage:
      'This image is still larger than {maxSize}MB after resizing. Please choose a smaller image.',
  },
});

function formatFileSizeMB(bytes: number): string {
  return (Math.ceil((bytes / 1_000_000) * 10) / 10).toFixed(1);
}

function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_SIZE_BYTES;
}

function validateFileType(file: File): boolean {
  // Check by MIME type first
  if (SUPPORTED_MIME_TYPES.includes(file.type)) {
    return true;
  }

  // Fallback to extension check if MIME type is not recognized
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension ? SUPPORTED_EXTENSIONS.includes(extension) : false;
}

function getFileValidationError(
  intl: IntlShape,
  file: File,
  shouldResizeOversizedImages: boolean,
): string | null {
  if (!validateFileType(file)) {
    return intl.formatMessage(messages.invalidFileType, {
      allowedTypes: SUPPORTED_EXTENSIONS.join(', '),
    });
  }

  if (
    !validateFileSize(file) &&
    !(shouldResizeOversizedImages && isCompressibleAttachmentImage(file))
  ) {
    const fileSizeMB = formatFileSizeMB(file.size);
    return intl.formatMessage(messages.fileSizeExceeded, {
      fileSize: fileSizeMB,
      maxSize: MAX_FILE_SIZE_MB,
    });
  }

  return null;
}

function useAttachmentUpload(attachmentSlotId: string) {
  const { accessCode } = useSignerContext();
  const { signatureRequestId } = useSignerAppParams();
  const client = useSignerAppClient();
  const queryClient = useQueryClient();
  const [validationError, setValidationError] = React.useState<string>();
  const intl = useIntl();
  const useV2Endpoint = useFeatureFlag(
    'sign_core_2026_08_31_attachment_upload_v2',
  );
  const shouldResizeOversizedImages = useFeatureFlag(
    'sign_core_2026_09_11_resize_signer_attachments',
  );

  const {
    mutate: uploadAttachmentMutation,
    error: uploadError,
    isLoading,
  } = useMutation<unknown, unknown, File>({
    mutationKey: [
      signatureRequestId,
      accessCode,
      'upload-attachment',
      attachmentSlotId,
    ] as const,
    mutationFn: async (file) => {
      const fileName = file.name;
      let fileToUpload = file;
      if (shouldResizeOversizedImages && !validateFileSize(file)) {
        try {
          fileToUpload = await resizeAttachmentImage(file, MAX_FILE_SIZE_BYTES);
        } catch (error) {
          if (error instanceof AttachmentImageResizeError) {
            if (error.code === 'decode-failed') {
              throw new Error(intl.formatMessage(messages.imageDecodeFailed));
            }
            if (error.code === 'output-too-large') {
              throw new Error(
                intl.formatMessage(messages.resizedImageTooLarge, {
                  maxSize: MAX_FILE_SIZE_MB,
                }),
              );
            }
            throw new Error(intl.formatMessage(messages.imageResizeFailed));
          }
          throw error;
        }
      }
      const uploadParams = {
        file: fileToUpload,
        csrfToken: getCSRFToken(),
      };
      try {
        const response = await (useV2Endpoint
          ? client.signerApi.uploadAttachmentV2(attachmentSlotId, uploadParams)
          : client.signerApi.uploadAttachment(attachmentSlotId, uploadParams));
        if (!response.success) {
          throw new Error(intl.formatMessage(messages.unknownError));
        }
      } catch (error) {
        if ((error as ResponseError)?.response != null) {
          const status = (error as ResponseError).response.status;
          if (status === 413) {
            throw new Error(
              intl.formatMessage(messages.fileSizeExceeded, {
                fileSize: formatFileSizeMB(fileToUpload.size),
                maxSize: MAX_FILE_SIZE_MB,
              }),
            );
          }
          if (status === 415) {
            throw new Error(
              intl.formatMessage(messages.invalidFileType, {
                allowedTypes: SUPPORTED_EXTENSIONS.join(', '),
              }),
            );
          }
        }
        throw error;
      }
      const uploadedAt = String(Date.now());
      queryClient.setQueryData(
        ['signer-experience', signatureRequestId, accessCode] as const,
        (
          prev: SignatureRequestResponse | undefined,
        ): SignatureRequestResponse | undefined => {
          if (!prev) {
            return prev;
          }
          return produce(prev, (draft) => {
            const slot = draft.signatureRequest.attachmentSlots.find(
              (slot) => slot.id === attachmentSlotId,
            );
            if (slot) {
              slot.uploadedAt = uploadedAt;
              slot.fileName = fileName;
            }
          });
        },
      );
    },
  });

  const uploadAttachment = React.useCallback(
    (file: File) => {
      // Clear any previous validation errors
      setValidationError(undefined);

      // Validate the file
      const validationErrorMessage = getFileValidationError(
        intl,
        file,
        shouldResizeOversizedImages,
      );
      if (validationErrorMessage) {
        setValidationError(validationErrorMessage);
        return;
      }

      // If validation passes, proceed with upload
      uploadAttachmentMutation(file);
    },
    [uploadAttachmentMutation, intl, shouldResizeOversizedImages],
  );

  const deleteAttachment = React.useCallback(() => {
    // Clear validation errors when deleting
    setValidationError(undefined);

    queryClient.setQueryData(
      ['signer-experience', signatureRequestId, accessCode] as const,
      (
        prev: SignatureRequestResponse | undefined,
      ): SignatureRequestResponse | undefined => {
        if (!prev) {
          return prev;
        }
        return produce(prev, (draft) => {
          const slot = draft.signatureRequest.attachmentSlots.find(
            (slot) => slot.id === attachmentSlotId,
          );
          if (slot) {
            slot.uploadedAt = undefined;
            slot.fileName = undefined;
          }
        });
      },
    );
  }, [signatureRequestId, accessCode, queryClient, attachmentSlotId]);

  const error = React.useMemo(() => {
    if (validationError) {
      return validationError;
    }

    if (uploadError) {
      return uploadError instanceof Error
        ? uploadError.message
        : intl.formatMessage(messages.unknownError);
    }

    return undefined;
  }, [uploadError, validationError, intl]);

  return {
    uploadAttachment,
    deleteAttachment,
    error,
    isLoading,
  };
}

function AttachmentDetails({
  fileName,
  isUploading,
  error,
  onDelete,
  attachmentId,
}: {
  fileName: string;
  isUploading?: boolean;
  error?: string;
  onDelete: () => void;
  attachmentId: string;
}) {
  return (
    <Stack width="100%" paddingTop="Macro XSmall">
      <Split wrap="nowrap" alignY="center" gap="Micro XSmall" isFlexGap>
        <Split.Item>
          <Box display="flex" alignItems="center">
            <UIIcon src={FileLine} />
          </Box>
        </Split.Item>
        <Split.Item marginRight="auto" width="fill" willCollapse>
          <Text
            size="medium"
            variant="label"
            isBold
            className={styles.fileName}
          >
            <Truncate location={0.3}>{fileName}</Truncate>
          </Text>
        </Split.Item>
        <Split.Item>
          <WhiteLabelThemeProvider mode="bright">
            {!isUploading && (
              <IconButton
                variant="transparent"
                onClick={onDelete}
                aria-describedby={`error-${attachmentId} required-${attachmentId} optional-${attachmentId} instructions-${attachmentId}`}
              >
                <UIIcon src={DeleteLine} aria-label="Delete attachment" />
              </IconButton>
            )}
          </WhiteLabelThemeProvider>
        </Split.Item>
      </Split>
      {isUploading && <ProgressBar isIndeterminate in />}
      {error && (
        <FormHelperText variant="alert" id={`error-${attachmentId}`}>
          {error}
        </FormHelperText>
      )}
    </Stack>
  );
}

export function AttachmentSlot({ attachment }: Props) {
  const { uploadAttachment, deleteAttachment, error, isLoading } =
    useAttachmentUpload(attachment.id);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [inputFileName, setInputFileName] = React.useState<string | null>(null);

  const filename = attachment.fileName || inputFileName;

  const handleDeleteAttachment = React.useCallback(() => {
    deleteAttachment();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      setInputFileName(null);
    }
  }, [deleteAttachment]);

  const { hasCustomStyles } = useBundleProps();

  return (
    <Box
      paddingBottom="32"
      paddingTop="Macro Medium"
      className={styles.attachmentSlot}
      width="100%"
    >
      <FormRow>
        <Text
          tagName="label"
          htmlFor={attachment.id}
          size="large"
          variant="label"
          isBold
        >
          {attachment.name}
        </Text>
        <Box display="none">
          <input
            ref={fileInputRef}
            type="file"
            id={`file-${attachment.id}`}
            data-testid={`file-input`}
            accept={SUPPORTED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setInputFileName(file.name);
                uploadAttachment(file);
              }
            }}
          />
        </Box>
        {attachment.required ? (
          <FormHelperText id={`required-${attachment.id}`}>
            <FormattedMessage
              id="2748d78342730728492dca00a65347fef0b9e7c0fc4a80a32a70047eaee428d8"
              defaultMessage="Required"
              description="text in signer flow of attachment page, tells signer the attachment is required"
            />
          </FormHelperText>
        ) : (
          <FormHelperText id={`optional-${attachment.id}`}>
            <FormattedMessage
              id="9545ef3058f7cfc7faf115854708efeca0bf6c33875f74ad0d31b2674ae911e8"
              defaultMessage="Optional"
              description="text in signer flow of attachment page, tells signer the attachment is optional"
            />
          </FormHelperText>
        )}
        {attachment.instructions && (
          <FormHelperText id={`instructions-${attachment.id}`}>
            <Text size="medium" color="subtle">
              {attachment.instructions}
            </Text>
          </FormHelperText>
        )}
        {filename ? (
          <AttachmentDetails
            fileName={filename}
            isUploading={isLoading}
            error={error}
            onDelete={handleDeleteAttachment}
            attachmentId={attachment.id}
          />
        ) : (
          <Box paddingTop="Macro XSmall">
            <WhiteLabelThemeProvider mode="bright">
              <Button
                className={styles.uploadButton}
                type="button"
                variant={hasCustomStyles ? 'outline' : 'opacity'}
                size="large"
                fullWidth
                withIconStart={<UIIcon src={UploadLine} />}
                aria-describedby={`instructions-${attachment.id} required-${attachment.id} optional-${attachment.id}`}
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.click();
                  }
                }}
                id={attachment.id}
              >
                <FormattedMessage
                  id="22e3b707eb2ac3435a8152908e9eed37d8a02c89e0624277191da8351fd911a7"
                  defaultMessage="Upload"
                  description="button text in signer flow of attachment page, signer can upload their document clicking it"
                />
              </Button>
            </WhiteLabelThemeProvider>
          </Box>
        )}
      </FormRow>
    </Box>
  );
}
